/**
 * The one notice that a change landed (Jason, 2026-10-07: "any time a user clicks save or set like on the account or
 * setting pages, or anywhere something changes dynamically, there needs to be something pop up that says updated or
 * save that is clear"). Pure: api.ts tells it after every change the API accepts (its words, or none, are api.ts's
 * `noticeWords`), and the portal's `SavedNotice` draws what it holds. Changes in quick succession (a drag on the design
 * board, typing that saves as it goes, the tree builder keeping its draft) share one notice, which stays while they
 * keep coming and fades a moment after the last: never a stack.
 */

/** What the notice shows: its words, and whether it is on or fading out. */
export interface NoticeState {
  words: string;
  on: boolean;
}

/** How long the notice stays after the last change, and how long it takes to fade (the drawing's own transition). */
export const HOLD_MS = 2400;
export const FADE_MS = 240;

export interface NoticeStore {
  /** A change landed: show these words, or keep showing the notice already up with them. */
  show: (words: string) => void;
  /** Take it down at once (a test, or a page that leaves). */
  clear: () => void;
  current: () => NoticeState | null;
  subscribe: (listener: () => void) => () => void;
}

export function createNotice({ hold = HOLD_MS, fade = FADE_MS }: { hold?: number; fade?: number } = {}): NoticeStore {
  let state: NoticeState | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  const set = (next: NoticeState | null) => {
    // The same notice again (a burst of the same change) is not news: nothing redraws.
    if (next === state || (next && state && next.words === state.words && next.on === state.on)) return;
    state = next;
    for (const l of listeners) l();
  };
  return {
    show(words) {
      clearTimeout(timer);
      set({ words, on: true });
      timer = setTimeout(() => {
        set({ words, on: false });
        timer = setTimeout(() => set(null), fade);
      }, hold);
    },
    clear() {
      clearTimeout(timer);
      timer = undefined;
      set(null);
    },
    current: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The portal's one notice. */
export const saved = createNotice();
